import { AlertCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import type { CardinalityConflict } from '@/lib/crm/core/types';

import { conflictKey } from './conflict-key';

const REDACTED_OWNER_LABEL = 'another record';

function buildOverwriteLabel(conflict: CardinalityConflict): string {
  const ownerName =
    conflict.existing_owner?.display_name ?? REDACTED_OWNER_LABEL;
  return `Reassign ${conflict.display_name} from ${ownerName} to this record – removes the existing association`;
}

interface ConflictItemProps {
  conflict: CardinalityConflict;
  /** Non-empty association type name — caller filters out conflicts without one. */
  assocTypeName: string;
  isAcked: boolean;
  onAcknowledge: (
    entityId: string,
    associationTypeName: string,
    checked: boolean
  ) => void;
  onRemoveTarget: (entityId: string, associationTypeName: string) => void;
}

function ConflictItem({
  conflict,
  assocTypeName,
  isAcked,
  onAcknowledge,
  onRemoveTarget,
}: ConflictItemProps) {
  const handleAcknowledge = useCallback(
    (checked: boolean) =>
      onAcknowledge(conflict.entity_id, assocTypeName, checked),
    [conflict.entity_id, assocTypeName, onAcknowledge]
  );

  const handleRemove = useCallback(
    () => onRemoveTarget(conflict.entity_id, assocTypeName),
    [conflict.entity_id, assocTypeName, onRemoveTarget]
  );

  const ownerName =
    conflict.existing_owner?.display_name ?? REDACTED_OWNER_LABEL;

  const overwriteLabel = useMemo(
    () => buildOverwriteLabel(conflict),
    // Depend on the primitive values so the string stays stable across
    // re-renders that produce a new conflict object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      conflict.entity_id,
      conflict.display_name,
      conflict.existing_owner?.display_name,
    ]
  );

  const entityBadgeState = useMemo(
    () => ({
      kind: 'data' as const,
      data: {
        id: conflict.entity_id,
        entityType: conflict.entity_type,
        displayName: conflict.display_name,
      },
    }),
    [conflict.entity_id, conflict.entity_type, conflict.display_name]
  );

  const checkboxLabel = useMemo(
    () => (
      <span className='text-sm font-medium text-[var(--color-text-error-primary)]'>
        {overwriteLabel}
      </span>
    ),
    [overwriteLabel]
  );

  return (
    <li className='flex flex-col gap-2 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3'>
      <div className='flex items-center gap-2'>
        <EntityBadge state={entityBadgeState} />
        <span className='text-sm text-[var(--color-text-secondary)]'>
          currently associated with{' '}
          <span className='font-medium text-[var(--color-text-primary)]'>
            {ownerName}
          </span>
        </span>
      </div>

      <div className='flex flex-wrap items-start gap-2'>
        <Checkbox
          isSelected={isAcked}
          onChange={handleAcknowledge}
          aria-label={overwriteLabel}
          label={checkboxLabel}
        />

        <Button color='secondary-destructive' size='sm' onClick={handleRemove}>
          Remove {conflict.display_name} from selection
        </Button>
      </div>
    </li>
  );
}

export interface ConflictResolutionPanelProps {
  conflicts: CardinalityConflict[];
  error: Error | null;
  /**
   * Set of compound conflict keys — `conflictKey(entity_id, association_type_name)` —
   * whose overwrite has been acknowledged. The compound key allows the same entity
   * to appear in two distinct association fields and be resolved independently.
   */
  acknowledged: Set<string>;
  /** Called when the user checks/unchecks the overwrite checkbox for a conflict. */
  onAcknowledge: (
    entityId: string,
    associationTypeName: string,
    checked: boolean
  ) => void;
  /** Called when the user clicks Remove on a conflict target. */
  onRemoveTarget: (entityId: string, associationTypeName: string) => void;
  /** Called when the user clicks Retry after an error. */
  onRetry?: () => void;
}

export const ConflictResolutionPanel = ({
  conflicts,
  error,
  acknowledged,
  onAcknowledge,
  onRemoveTarget,
  onRetry,
}: ConflictResolutionPanelProps) => {
  // Drop conflicts missing an association_type_name — Remove/Overwrite actions
  // are keyed by (entity_id, association_type_name) and would silently target
  // the wrong conflict (or no-op) if dispatched without one. The backend
  // validate endpoint always emits it; this is a defensive fail-closed guard
  // for older error payloads that predate type-name enrichment.
  const actionableConflicts = useMemo(
    () =>
      conflicts.filter(
        c =>
          typeof c.association_type_name === 'string' &&
          c.association_type_name !== ''
      ),
    [conflicts]
  );
  const droppedCount = conflicts.length - actionableConflicts.length;
  useEffect(() => {
    if (droppedCount > 0) {
      console.warn(
        'ConflictResolutionPanel: dropped %d conflict(s) missing association_type_name',
        droppedCount
      );
    }
  }, [droppedCount]);

  useEffect(() => {
    if (error) {
      console.error('ConflictResolutionPanel: conflict check failed', error);
    }
  }, [error]);

  return (
    <div
      role='alert'
      aria-live='assertive'
      aria-label='Association conflicts'
      className='flex flex-col gap-3'
    >
      {error !== null && (
        <div className='flex items-start gap-2 rounded-lg border border-[var(--color-error-200)] bg-[var(--color-error-50)] p-3 text-sm text-[var(--color-text-error-primary)]'>
          <AlertCircle
            aria-hidden='true'
            className='mt-0.5 size-4 shrink-0 text-[var(--color-error-600)]'
          />
          <div className='flex flex-1 flex-col gap-1'>
            <span className='font-medium'>We couldn't check for conflicts</span>
            {onRetry !== undefined && (
              <Button
                color='tertiary-destructive'
                size='sm'
                onClick={onRetry}
                className='self-start'
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      )}

      {actionableConflicts.length > 0 && (
        <section aria-labelledby='conflict-panel-heading'>
          <h2
            id='conflict-panel-heading'
            className='mb-2 text-sm font-semibold text-[var(--color-text-primary)]'
          >
            Association conflicts
          </h2>
          <ul className='flex flex-col gap-3' role='list'>
            {actionableConflicts.map(conflict => {
              // Filter above guarantees a non-empty string here.
              const assocTypeName = conflict.association_type_name as string;
              return (
                <ConflictItem
                  key={conflictKey(conflict.entity_id, assocTypeName)}
                  conflict={conflict}
                  assocTypeName={assocTypeName}
                  isAcked={acknowledged.has(
                    conflictKey(conflict.entity_id, assocTypeName)
                  )}
                  onAcknowledge={onAcknowledge}
                  onRemoveTarget={onRemoveTarget}
                />
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
};
